/* =====================================================================
 * Button — 공통 버튼 컴포넌트
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';
import { workspaceButtonStyles } from './workspaceSurface';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'surface';
type Size = 'sm' | 'md' | 'lg' | 'icon';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

const sizeStyles = {
  sm: css`
    padding: 4px 12px;
    font-size: 0.8125rem;
    height: 32px;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  `,
  md: css`
    padding: 8px 16px;
    font-size: 0.875rem;
    height: 40px;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  `,
  lg: css`
    padding: 12px 24px;
    font-size: 1rem;
    height: 48px;
    border-radius: ${({ theme }) => theme.sizes.borderRadius};
  `,
  icon: css`
    padding: 8px;
    width: 36px;
    height: 36px;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  `,
};

const variantStyles = {
  surface: workspaceButtonStyles,
  primary: css`
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.textInverse};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.accentHover};
    }
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.bgSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.bgHover};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.textSecondary};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.bgHover};
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  `,
  danger: css`
    background: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.textInverse};
    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  `,
};

const StyledButton = styled.button<{
  $variant: Variant;
  $size: Size;
  $fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  user-select: none;

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }
`;

const Loader = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading,
  leftIcon,
  rightIcon,
  fullWidth = false,
  children,
  disabled,
  ...props
}) => (
  <StyledButton
    $variant={variant}
    $size={size}
    $fullWidth={fullWidth}
    disabled={disabled || isLoading}
    {...props}
  >
    {isLoading ? <Loader /> : leftIcon}
    {children}
    {!isLoading && rightIcon}
  </StyledButton>
);
